import { useCallback, useEffect, useRef, useState } from "react";
import { ring, stopRing } from "@/utils/audio";
import {
  askNotificationPermission, notifyAskedBefore, notificationPermission, notificationsSupported,
  notifyDesktop, rememberNotifyAsked, shouldOfferNotifications, startTitleAlert, stopTitleAlert,
} from "@/lib/attention";
import { enablePushNotifications, PUSH_SUPPORTED } from "@/lib/pushNotifications";

// Being rung. The server already tells this end: call_started carries
// dm_participants for a direct call, call_invited targets the invitee. What was
// missing was everything after that.
//
// What is modelled here is deliberately thin. kMeet is external, so the server
// never learns who entered a room; "answered" and "in progress" are states it
// cannot observe and must not invent. The whole truth available is "someone
// invited you at time T", plus expiry — so there are exactly two outcomes,
// ringing and missed, and missed is decided locally when the window passes.
//
// Lives in a hook rather than in Workspace because the page cannot be rendered
// in a test without half the app, and this is the part that failed in real use.

export const MISSED_AFTER_MS = 30000;   // matches the ring's own hard stop

export function useIncomingCall({ subscribe, userId, inCall = false }) {
  const [incoming, setIncoming] = useState(null);  // { room_name, from, kind, at, status, muted }
  const desktopRef = useRef(null);

  // Everything that reaches past the card, undone in one place. A tab still
  // flashing for a call that is over is worse than one that never flashed.
  const stopAttention = useCallback(() => {
    stopRing();
    stopTitleAlert();
    try { desktopRef.current?.close(); } catch { /* already gone */ }
    desktopRef.current = null;
  }, []);

  useEffect(() => stopAttention, [stopAttention]);

  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe((ev) => {
      if (!userId || !ev?.room_name) return;
      let next = null;
      if (ev.type === "call_started") {
        // A channel call is addressed to nobody in particular; its card in the
        // channel is the trace. Only a direct call rings, and only the callee.
        if (!ev.dm_participants?.includes(userId) || ev.started_by?.id === userId) return;
        next = { room_name: ev.room_name, from: ev.started_by, kind: "dm" };
      } else if (ev.type === "call_invited") {
        if (ev.invited_by?.id === userId) return;
        next = { room_name: ev.room_name, from: ev.invited_by, kind: "invite" };
      }
      if (!next) return;

      const who = next.from?.name || "Un coleg";
      const what = next.kind === "dm" ? `${who} te sună` : `${who} te invită la un apel`;

      stopAttention();
      setIncoming({ ...next, at: Date.now(), status: "ringing", muted: false });
      startTitleAlert(what);

      // Sound can be refused: a tab that has never been clicked keeps its
      // AudioContext suspended, and that silence is what "the other one hears
      // nothing" turned out to be. The card says so rather than leaving it a
      // mystery the person blames on the app.
      ring({ subtle: inCall }).then(
        (played) => setIncoming((c) => (c ? { ...c, muted: !played } : c)),
        () => setIncoming((c) => (c ? { ...c, muted: true } : c)));

      // No permission prompt here. Being rung is the worst moment to ask: the
      // person is reacting to a call, not reading a dialog, and a reflex refusal
      // is permanent. notifyDesktop is a no-op without permission, so this
      // raises one only for someone who already agreed; the asking happens after
      // a call has actually been missed, where the question answers itself.
      desktopRef.current = notifyDesktop({
        title: what,
        body: "Apasă ca să răspunzi în Qedara Chat.",
        tag: `call-${next.room_name}`,
      });
    });
  }, [subscribe, userId, inCall, stopAttention]);

  useEffect(() => {
    if (incoming?.status !== "ringing") return undefined;
    const t = setTimeout(() => {
      stopAttention();
      // The offer rides on the card, decided at the moment it becomes missed:
      // a closed tab is reachable only by push, and this is the one moment the
      // person has evidence for why that matters.
      const offer = shouldOfferNotifications({
        supported: notificationsSupported(),
        permission: notificationPermission(),
        askedBefore: notifyAskedBefore(),
      });
      setIncoming((c) => (c && c.status === "ringing"
        ? { ...c, status: "missed", offerNotifications: offer } : c));
    }, MISSED_AFTER_MS);
    return () => clearTimeout(t);
  }, [incoming?.status, incoming?.at, stopAttention]);

  /**
   * Yes, notify me next time. Push first, because the gap this closes is a tab
   * that is not open at all and a page-level Notification cannot reach that.
   * Plain permission is the fallback for a browser without push, which at least
   * covers a backgrounded tab.
   */
  const enableNotifications = useCallback(async () => {
    rememberNotifyAsked();
    setIncoming((c) => (c ? { ...c, offerNotifications: false } : c));
    try {
      if (PUSH_SUPPORTED) return await enablePushNotifications();
      return await askNotificationPermission();
    } catch {
      return "error";
    }
  }, []);

  /** No. Recorded, and not raised again. */
  const declineNotifications = useCallback(() => {
    rememberNotifyAsked();
    setIncoming((c) => (c ? { ...c, offerNotifications: false } : c));
  }, []);

  // The card stays after a missed call, as the trace; clear() is the dismiss.
  const clear = useCallback(() => { stopAttention(); setIncoming(null); }, [stopAttention]);

  return { incoming, clear, stopAttention, enableNotifications, declineNotifications };
}
